import type { ScaleLinear } from "d3";
import { equals } from "ramda";
import { PureComponent } from "react";
import { connect } from "react-redux";

import { rangeEquals } from "@/app-utils/range.ts";
import { type IPileup, PileupFactory } from "@/app-utils/track-shapes/track-pileup-factory.ts";
import { getPileupHeight } from "@/app-utils/track-shapes/track-utils.ts";
import type { BlastHit, Contig, JobResult, Range, Track } from "@/graphql.ts";
import type { IGenomeViewerStateLayout } from "@/redux/modules/genome-browser.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { DEFAULT_TRACK_HEIGHT } from "@/settings/settings.js";
import { hspShapePileupParams } from "../blast-result/HspShape.js";
import { BlastHitTrack } from "./BlastHitTrack.js";
import type { SetTooltipContent } from "./GenomeViewerTracksList.js";

interface IBlastHitTrackLoaderOwnProps {
  track: Track; // track spec
  jobResult: JobResult;
  scale: ScaleLinear<number, number>;
  setTooltipContent: SetTooltipContent;
}

interface IBlastHitTrackLoaderAppStateProps {
  range: Range;
  contig: Contig;
  layout: IGenomeViewerStateLayout;
}

interface IBlastHitTrackLoaderDispatchProps {}

interface IBlastHitTrackLoaderProps
  extends IBlastHitTrackLoaderAppStateProps,
    IBlastHitTrackLoaderDispatchProps,
    IBlastHitTrackLoaderOwnProps {}

function mapStateToProps(state: IAppState): IBlastHitTrackLoaderAppStateProps {
  return {
    range: state.genomeBrowser.range,
    contig: state.genomeBrowser.contig,
    layout: state.genomeBrowser.layout,
  };
}

function mapDispatchToProps(_dispatch): IBlastHitTrackLoaderDispatchProps {
  return {};
}

interface IBlastHitTrackLoaderState {
  noHit: boolean;
  pileup: IPileup;
}

class BlastHitTrackLoader extends PureComponent<IBlastHitTrackLoaderProps, IBlastHitTrackLoaderState> {
  private pileupFactory: PileupFactory;

  constructor(props: IBlastHitTrackLoaderProps) {
    super(props);

    this.state = {
      noHit: false,
      pileup: null,
    };
  }

  componentDidMount() {
    this.pileupFactory = new PileupFactory(this.props.scale);

    this.setState({
      noHit: !this.jobResultsMatchContig(),
    });

    this.updateLayout();
  }

  jobResultsMatchContig = () => {
    if (this.props.jobResult) {
      const hit: BlastHit = this.props.jobResult.blastHits.find((h) => this.props.contig?.id === h?.target?.id);
      return !!hit;
    }
    return false;
  };

  componentDidUpdate(prevProps: Readonly<IBlastHitTrackLoaderProps>, prevState: Readonly<IBlastHitTrackLoaderState>) {
    if (this.props.scale !== prevProps.scale) {
      this.pileupFactory = new PileupFactory(this.props.scale);
    }

    const rangeDiffers = !rangeEquals(prevProps.range, this.props.range);
    const contigDiffers: boolean = prevProps.contig?.id !== this.props.contig?.id;
    const jobResultDiffers: boolean = prevProps.jobResult !== this.props.jobResult;
    const boxModelDiffers = !equals(this.props.layout, prevProps.layout);

    if (contigDiffers || jobResultDiffers) {
      this.setState({ noHit: !this.jobResultsMatchContig() });
    }

    const contigMismatchDiffers: boolean = this.state.noHit !== prevState.noHit;
    // update the layout
    if (rangeDiffers || contigMismatchDiffers || jobResultDiffers || boxModelDiffers) {
      this.updateLayout();
    }
  }

  updateLayout = () => {
    if (this.state.noHit) {
      this.setState({ pileup: null });
      return;
    }

    const hit: BlastHit = this.props.jobResult?.blastHits.find((h) => this.props.contig?.id === h?.target?.id);
    if (hit) {
      const pileup: IPileup = this.pileupFactory.buildHspPileup(hit, this.props.range, hspShapePileupParams);
      this.setState({ pileup: pileup });
    }
  };

  getTrackHeight = () => {
    const height = getPileupHeight(this.state.pileup);
    return Math.max(DEFAULT_TRACK_HEIGHT, height);
  };

  render() {
    return (
      <BlastHitTrack
        range={this.props.range}
        track={this.props.track}
        trackHeight={this.getTrackHeight()}
        pileup={this.state.pileup}
        noHits={this.state.noHit}
        setTooltipContent={this.props.setTooltipContent}
      />
    );
  }
}

const SmartBlastHitTrackLoader = connect<
  IBlastHitTrackLoaderAppStateProps,
  IBlastHitTrackLoaderDispatchProps,
  IBlastHitTrackLoaderOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(BlastHitTrackLoader);

export { SmartBlastHitTrackLoader as BlastHitTrackLoader };
